import { SendListingFormPanel } from '../components/SendListingForm';
import SupplierEmailPausePanel from '../components/SupplierEmailPausePanel';
import TeamSignaturesPanel from '../components/TeamSignaturesPanel';
import EmailDomainsPanel from '../components/EmailDomainsPanel';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { useAuth } from '../context/AuthContext';
import { BrandSettingsCard } from '../components/BrandSettingsCard';
import { SenderIdentitiesCard } from '../components/SenderIdentitiesCard';
import { 
  Target,
  Swords,
  Users,
  Plus,
  Trash2,
  Save,
  Loader2,
  Building2,
  Globe,
  Linkedin,
  Plug,
  CheckCircle,
  XCircle,
  Mail,
  Eye,
  Clock,
  Key,
  Unlink,
  Ghost,
  Copy,
  CalendarClock,
  ShieldCheck,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [userIntegrations, setUserIntegrations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState({ name: '', domain: '', linkedin_url: '' });
  
  // Integration keys state
  const [phantombusterKey, setPhantombusterKey] = useState('');
  const [hubspotKey, setHubspotKey] = useState('');
  const [companiesHouseKey, setCompaniesHouseKey] = useState('');
  const [hunterKey, setHunterKey] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [emailSignature, setEmailSignature] = useState('');
  const [savingIntegration, setSavingIntegration] = useState(null);
  const [savingSenderEmail, setSavingSenderEmail] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);
  const [showSignaturePreview, setShowSignaturePreview] = useState(false);

  // HubSpot import state
  const [hsImportJob, setHsImportJob] = useState(null);
  const [startingImport, setStartingImport] = useState(false);

  // Better Proposals state
  const [bpConnected, setBpConnected] = useState(false);
  const [bpAutoSync, setBpAutoSync] = useState(null);
  const [syncingBp, setSyncingBp] = useState(false);
  const [pendingContacts, setPendingContacts] = useState([]);
  const [approvingId, setApprovingId] = useState(null);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [bpToken, setBpToken] = useState('');
  const [savingBp, setSavingBp] = useState(false);

  // Email booking button state
  const [bookingButtonEnabled, setBookingButtonEnabled] = useState(true);
  const [defaultBookingPageId, setDefaultBookingPageId] = useState('');
  const [bookingPages, setBookingPages] = useState([]);
  const [publicAppUrlConfigured, setPublicAppUrlConfigured] = useState(true);
  const [savingBookingBtn, setSavingBookingBtn] = useState(false);
  const [enquiryOptionsText, setEnquiryOptionsText] = useState('');
  const [savingEnquiry, setSavingEnquiry] = useState(false);
  const [complianceStats, setComplianceStats] = useState({});
  const [outlookSync, setOutlookSync] = useState({});
  const [syncingOutlook, setSyncingOutlook] = useState(false);
  
  // Admin state
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  // Team invites
  const [invites, setInvites] = useState([]);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteAdmin, setInviteAdmin] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  
  // Generate the webhook URL with user ID
  const webhookUrl = user?.id ? `${API}/rb2b/webhook/${user.id}` : '';

  useEffect(() => {
    fetchSettings();
    fetchIntegrations();
    fetchUserIntegrations();
    fetchBookingButtonSettings();
    fetchHubspotImportStatus();
    fetchBpStatus();
    fetchPendingContacts();
  }, []);

  // Poll HubSpot import while running
  useEffect(() => {
    if (!hsImportJob || !['queued', 'running'].includes(hsImportJob.status)) return;
    const t = setInterval(fetchHubspotImportStatus, 3000);
    return () => clearInterval(t);
  }, [hsImportJob]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchAdminUsers();
      fetchInvites();
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations/status`);
      setIntegrations(response.data);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
    }
  };

  const fetchUserIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/user/integrations`);
      setUserIntegrations(response.data);
      // Pre-fill sender email if it exists
      if (response.data.sender_email) {
        setSenderEmail(response.data.sender_email);
      }
      // Pre-fill email signature if it exists
      if (response.data.email_signature) {
        setEmailSignature(response.data.email_signature);
      }
    } catch (error) {
      console.error('Failed to fetch user integrations:', error);
    }
  };

  const fetchBookingButtonSettings = async () => {
    try {
      const [s, p, eo] = await Promise.all([
        axios.get(`${API}/email-settings/booking-button`),
        axios.get(`${API}/booking-pages`),
        axios.get(`${API}/email-settings/enquiry-options`),
      ]);
      setBookingButtonEnabled(s.data.booking_button_enabled);
      setDefaultBookingPageId(s.data.default_booking_page_id || '');
      setPublicAppUrlConfigured(s.data.public_app_url_configured);
      setBookingPages(p.data.booking_pages || []);
      setEnquiryOptionsText((eo.data.options || []).join('\n'));
      try {
        const cs = await axios.get(`${API}/compliance/suppression/stats`);
        setComplianceStats(cs.data || {});
      } catch { /* compliance stats optional */ }
      try {
        const os = await axios.get(`${API}/outlook/sync-status`);
        setOutlookSync(os.data || {});
      } catch { /* outlook sync status optional */ }
    } catch (error) {
      console.error('Failed to fetch booking button settings:', error);
    }
  };

  const saveEnquiryOptions = async () => {
    const options = enquiryOptionsText.split('\n').map(s => s.trim()).filter(Boolean);
    if (options.length === 0) { toast.error('Add at least one enquiry option'); return; }
    setSavingEnquiry(true);
    try {
      const res = await axios.put(`${API}/email-settings/enquiry-options`, { options });
      setEnquiryOptionsText((res.data.options || options).join('\n'));
      toast.success('Enquiry options saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingEnquiry(false);
    }
  };

  const saveBookingButtonSettings = async () => {
    setSavingBookingBtn(true);
    try {
      await axios.put(`${API}/email-settings/booking-button`, {
        booking_button_enabled: bookingButtonEnabled,
        default_booking_page_id: defaultBookingPageId || null,
      });
      toast.success('Booking button settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingBookingBtn(false);
    }
  };

  const syncOutlookNow = async () => {
    setSyncingOutlook(true);
    try {
      const res = await axios.post(`${API}/outlook/sync-sent`);
      if (res.data.ok && res.data.first_run) {
        toast.success('Outlook sync enabled - new sent emails from now on will be logged.');
      } else if (res.data.ok) {
        toast.success(`Synced: ${res.data.created_contacts || 0} new contact(s), ${res.data.logged_emails || 0} sent + ${res.data.logged_inbound || 0} reply(ies) logged.`);
      } else if (res.data.reason === 'not_connected') {
        toast.error('Connect (or reconnect) your Outlook in Meetings first.');
      } else {
        toast.error(`Sync unavailable: ${res.data.reason}`);
      }
      const st = await axios.get(`${API}/outlook/sync-status`);
      setOutlookSync(st.data || {});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncingOutlook(false);
    }
  };

  const toggleOutlookSync = async (enabled) => {
    try {
      await axios.post(`${API}/outlook/sync-toggle?enabled=${enabled}`);
      setOutlookSync(prev => ({ ...prev, enabled }));
      toast.success(enabled ? 'Outlook email logging on' : 'Outlook email logging paused');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const fetchAdminUsers = async () => {
    if (!user?.is_admin) return;
    setLoadingAdmin(true);
    try {
      const response = await axios.get(`${API}/auth/admin/users`);
      setAdminUsers(response.data?.users || response.data || []);
    } catch (error) {
      console.error('Failed to fetch admin users:', error);
    } finally {
      setLoadingAdmin(false);
    }
  };

  const fetchInvites = async () => {
    if (!user?.is_admin) return;
    try {
      const res = await axios.get(`${API}/auth/admin/team/invites`);
      setInvites(res.data?.invites || []);
    } catch (e) { console.error('Failed to fetch invites', e); }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) { toast.error('Enter an email address'); return; }
    setSendingInvite(true);
    try {
      await axios.post(`${API}/auth/admin/team/invite`, { name: inviteName.trim(), email: inviteEmail.trim(), make_admin: inviteAdmin });
      toast.success(`Invitation emailed to ${inviteEmail.trim()}`);
      setInviteName(''); setInviteEmail(''); setInviteAdmin(false);
      fetchInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send invite');
    } finally { setSendingInvite(false); }
  };

  const resendInvite = async (id) => {
    try { await axios.post(`${API}/auth/admin/team/invites/${id}/resend`); toast.success('Invitation re-sent'); }
    catch { toast.error('Could not resend'); }
  };

  const revokeInvite = async (id) => {
    try { await axios.delete(`${API}/auth/admin/team/invites/${id}`); toast.success('Invitation revoked'); fetchInvites(); }
    catch { toast.error('Could not revoke'); }
  };

  const toggleUserAdmin = async (u) => {
    try {
      const res = await axios.post(`${API}/auth/admin/toggle-admin/${u.id}`);
      toast.success(res.data?.is_admin ? `${u.name || u.email} is now an admin` : `${u.name || u.email} is no longer an admin`);
      fetchAdminUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update role'); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email} from the team? They will lose access immediately.`)) return;
    try {
      await axios.delete(`${API}/auth/admin/users/${u.id}`);
      toast.success(`${u.name || u.email} removed`);
      fetchAdminUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not remove user'); }
  };

  const handleAdminResetPassword = async () => {
    if (!resetEmail.trim() || !resetPassword.trim()) {
      toast.error('Please enter both email and new password');
      return;
    }
    if (resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResettingPassword(true);
    try {
      await axios.post(`${API}/auth/admin/reset-password`, {
        user_email: resetEmail,
        new_password: resetPassword
      });
      toast.success(`Password reset successfully for ${resetEmail}`);
      setResetEmail('');
      setResetPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings`, {
        icp_criteria: settings.icp_criteria,
        competitors: settings.competitors,
        daily_send_cap: settings.daily_send_cap
      });
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const savePhantombusterKey = async () => {
    if (!phantombusterKey.trim()) {
      toast.error('Please enter your Phantombuster API key');
      return;
    }
    setSavingIntegration('phantombuster');
    try {
      await axios.post(`${API}/user/integrations`, {
        phantombuster_api_key: phantombusterKey
      });
      toast.success('Phantombuster connected successfully');
      setPhantombusterKey('');
      fetchUserIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Phantombuster');
    } finally {
      setSavingIntegration(null);
    }
  };

  const saveHubspotKey = async () => {
    if (!hubspotKey.trim()) {
      toast.error('Please enter your HubSpot API key');
      return;
    }
    setSavingIntegration('hubspot');
    try {
      await axios.post(`${API}/user/integrations`, {
        hubspot_api_key: hubspotKey
      });
      toast.success('HubSpot connected successfully');
      setHubspotKey('');
      fetchUserIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect HubSpot');
    } finally {
      setSavingIntegration(null);
    }
  };

  const fetchHubspotImportStatus = async () => {
    try {
      const res = await axios.get(`${API}/hubspot-import/status`);
      setHsImportJob(res.data.job);
    } catch (e) { /* ignore */ }
  };

  const cancelHubspotImport = async () => {
    try {
      await axios.post(`${API}/hubspot-import/cancel`);
      toast.success('Cancelling import…');
      fetchHubspotImportStatus();
    } catch (error) {
      toast.error('Failed to cancel import');
    }
  };

  const startHubspotImport = async (resume = false) => {
    setStartingImport(true);
    try {
      const body = resume ? { resume: true } : (hubspotKey.trim() ? { token: hubspotKey.trim() } : {});
      await axios.post(`${API}/hubspot-import/start`, body);
      toast.success(resume ? 'Resuming import…' : 'Import started - this runs in the background.');
      setHubspotKey('');
      fetchHubspotImportStatus();
      fetchUserIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start import');
    } finally {
      setStartingImport(false);
    }
  };

  const fetchBpStatus = async () => {
    try {
      const res = await axios.get(`${API}/better-proposals/status`);
      setBpConnected(!!res.data.connected);
      if (res.data.connected) {
        try {
          const s = await axios.get(`${API}/better-proposals/auto-sync-status`);
          setBpAutoSync(s.data);
        } catch { /* ignore */ }
      }
    } catch (e) { /* ignore */ }
  };

  const connectBp = async () => {
    if (!bpToken.trim()) { toast.error('Enter your Better Proposals API token'); return; }
    setSavingBp(true);
    try {
      await axios.post(`${API}/better-proposals/connect`, { token: bpToken.trim() });
      toast.success('Better Proposals connected');
      setBpToken('');
      setBpConnected(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Better Proposals');
    } finally {
      setSavingBp(false);
    }
  };

  const fetchPendingContacts = async () => {
    try {
      const res = await axios.get(`${API}/contact-approvals?status=pending`);
      setPendingContacts(res.data.approvals || []);
    } catch { /* ignore */ }
  };

  const approveContact = async (id) => {
    setApprovingId(id);
    try {
      await axios.post(`${API}/contact-approvals/${id}/approve`);
      toast.success('Contact added to your CRM');
      setPendingContacts((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not approve contact');
    } finally { setApprovingId(null); }
  };

  const rejectContact = async (id) => {
    setApprovingId(id);
    try {
      await axios.post(`${API}/contact-approvals/${id}/reject`);
      toast('Contact dismissed - it won\u2019t be suggested again');
      setPendingContacts((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not dismiss contact');
    } finally { setApprovingId(null); }
  };

  const approveAllContacts = async () => {
    setBulkApproving(true);
    try {
      const res = await axios.post(`${API}/contact-approvals/approve-all`);
      toast.success(`Added ${res.data.approved} contact${res.data.approved === 1 ? '' : 's'} to your CRM`);
      setPendingContacts([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not approve contacts');
    } finally { setBulkApproving(false); }
  };


  const syncBpNow = async () => {
    setSyncingBp(true);
    try {
      const res = await axios.post(`${API}/better-proposals/sync-deals?months=12`);
      const { created = 0, updated = 0 } = res.data || {};
      toast.success(`Synced - ${updated} deal${updated === 1 ? '' : 's'} updated, ${created} new.`);
      fetchBpStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed - check your connection.');
    } finally {
      setSyncingBp(false);
    }
  };

  const disconnectBp = async () => {
    setSavingBp(true);
    try {
      await axios.delete(`${API}/better-proposals/disconnect`);
      setBpConnected(false);
      toast.success('Better Proposals disconnected');
    } catch (error) {
      toast.error('Failed to disconnect');
    } finally {
      setSavingBp(false);
    }
  };


  const saveSenderEmail = async () => {
    if (!senderEmail.trim()) {
      toast.error('Please enter your sender email address');
      return;
    }
    // Basic email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(senderEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSavingSenderEmail(true);
    try {
      await axios.post(`${API}/user/integrations`, {
        sender_email: senderEmail
      });
      toast.success('Sender email saved successfully');
      fetchUserIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sender email');
    } finally {
      setSavingSenderEmail(false);
    }
  };

  const saveEmailSignature = async () => {
    setSavingSignature(true);
    try {
      await axios.post(`${API}/user/integrations`, {
        email_signature: emailSignature
      });
      toast.success('Email signature saved successfully');
      fetchUserIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email signature');
    } finally {
      setSavingSignature(false);
    }
  };

  const saveCompaniesHouseKey = async () => {
    if (!companiesHouseKey.trim()) {
      toast.error('Please enter your Companies House API key');
      return;
    }
    setSavingIntegration('companies_house');
    try {
      await axios.post(`${API}/user/integrations`, {
        companies_house_api_key: companiesHouseKey
      });
      toast.success('Companies House connected successfully');
      setCompaniesHouseKey('');
      fetchUserIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Companies House');
    } finally {
      setSavingIntegration(null);
    }
  };

  const saveHunterKey = async () => {
    if (!hunterKey.trim()) {
      toast.error('Please enter your Hunter.io API key');
      return;
    }
    setSavingIntegration('hunter');
    try {
      await axios.post(`${API}/user/integrations`, {
        hunter_api_key: hunterKey
      });
      toast.success('Hunter.io connected successfully');
      setHunterKey('');
      fetchUserIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Hunter.io');
    } finally {
      setSavingIntegration(null);
    }
  };

  const disconnectIntegration = async (type) => {
    setSavingIntegration(type);
    try {
      await axios.delete(`${API}/user/integrations/${type}`);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} disconnected`);
      fetchUserIntegrations();
    } catch (error) {
      toast.error(`Failed to disconnect ${type}`);
    } finally {
      setSavingIntegration(null);
    }
  };

  const updateICPCriteria = (field, value) => {
    setSettings({
      ...settings,
      icp_criteria: {
        ...settings.icp_criteria,
        [field]: value
      }
    });
  };

  const addCompetitor = () => {
    if (!newCompetitor.name) {
      toast.error('Competitor name is required');
      return;
    }
    setSettings({
      ...settings,
      competitors: [...(settings.competitors || []), newCompetitor]
    });
    setNewCompetitor({ name: '', domain: '', linkedin_url: '' });
  };

  const removeCompetitor = (index) => {
    const updated = [...settings.competitors];
    updated.splice(index, 1);
    setSettings({ ...settings, competitors: updated });
  };

  const toggleSICCode = (code) => {
    const current = settings.icp_criteria?.sic_codes || [];
    const updated = current.includes(code)
      ? current.filter(c => c !== code)
      : [...current, code];
    updateICPCriteria('sic_codes', updated);
  };

  const sicCodes = ['Marketing', 'Design', 'Advertising', 'Brand', 'Tech', 'Media', 'PR', 'Creative'];

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 w-48 bg-slate-800 rounded" />
        <div className="h-64 bg-slate-800 rounded-xl" />
        <div className="h-64 bg-slate-800 rounded-xl" />
      </div>
    );
  }

  const wsAdmin = !!user?.is_admin || ['owner', 'admin'].includes(String(user?.role || '').toLowerCase());
  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-serif text-4xl text-white mb-2">Settings</h1>
          <p className="text-slate-400">{wsAdmin ? 'Configure your ICP criteria and integrations' : 'Your signature, sending address, the supplier email pause and your Outlook connection. Other settings are managed by your admin.'}</p>
        </div>
        {wsAdmin && <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>}
      </div>

      {/* Brand */}
      <TeamSignaturesPanel isAdmin={wsAdmin} />
      <SupplierEmailPausePanel />
      {wsAdmin && (<>
      <EmailDomainsPanel />
      <BrandSettingsCard isAdmin={!user?.role || ['owner', 'admin'].includes(String(user?.role).toLowerCase())} />

      {/* Sender identities */}
      <SenderIdentitiesCard isAdmin={!user?.role || ['owner', 'admin'].includes(String(user?.role).toLowerCase())} />

      {/* Your Integrations - API Keys */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-500/10 rounded-lg">
              <Key className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">Your Integrations</CardTitle>
              <CardDescription>Connect your accounts for email finding and outreach automation</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Phantombuster */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Ghost className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="font-medium text-white">Phantombuster</p>
                  <p className="text-xs text-slate-500">Email finding & LinkedIn automation</p>
                </div>
              </div>
              {userIntegrations?.phantombuster_connected ? (
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-500/20 text-emerald-400">Connected</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => disconnectIntegration('phantombuster')}
                    disabled={savingIntegration === 'phantombuster'}
                    className="text-slate-400 hover:text-red-400"
                  >
                    {savingIntegration === 'phantombuster' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Unlink className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ) : (
                <Badge className="bg-slate-700/50 text-slate-400">Not connected</Badge>
              )}
            </div>
            {!userIntegrations?.phantombuster_connected && (
              <div className="flex gap-2">
                <Input
                  type="password"
                  value={phantombusterKey}
                  onChange={(e) => setPhantombusterKey(e.target.value)}
                  placeholder="Enter your Phantombuster API key"
                  className="bg-slate-950 border-slate-800 flex-1"
                  data-testid="phantombuster-key-input"
                />
                <Button
                  onClick={savePhantombusterKey}
                  disabled={savingIntegration === 'phantombuster'}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="connect-phantombuster-btn"
                >
                  {savingIntegration === 'phantombuster' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Connect'
                  )}
                </Button>
              </div>
            )}
            {userIntegrations?.phantombuster_email && (
              <p className="text-xs text-slate-500">Connected as: {userIntegrations.phantombuster_email}</p>
            )}
            <p className="text-xs text-slate-600">
              Get your API key from{' '}
              <a href="https://phantombuster.com/account/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                Phantombuster Settings
              </a>
            </p>
          </div>

          <Separator className="bg-slate-800" />

          {user?.is_admin && (
          <>
          {/* HubSpot */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-orange-400" />
                <div>
                  <p className="font-medium text-white">HubSpot</p>
                  <p className="text-xs text-slate-500">Send emails & track engagement</p>
                </div>
              </div>
              {userIntegrations?.hubspot_connected ? (
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-500/20 text-emerald-400">Connected</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => disconnectIntegration('hubspot')}
                    disabled={savingIntegration === 'hubspot'}
                    className="text-slate-400 hover:text-red-400"
                  >
                    {savingIntegration === 'hubspot' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Unlink className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ) : (
                <Badge className="bg-slate-700/50 text-slate-400">Not connected</Badge>
              )}
            </div>
            {!userIntegrations?.hubspot_connected && (
              <div className="flex gap-2">
                <Input
                  type="password"
                  value={hubspotKey}
                  onChange={(e) => setHubspotKey(e.target.value)}
                  placeholder="Enter your HubSpot Private App token"
                  className="bg-slate-950 border-slate-800 flex-1"
                  data-testid="hubspot-key-input"
                />
                <Button
                  onClick={saveHubspotKey}
                  disabled={savingIntegration === 'hubspot'}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="connect-hubspot-btn"
                >
                  {savingIntegration === 'hubspot' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Connect'
                  )}
                </Button>
              </div>
            )}
            <p className="text-xs text-slate-600">
              Create a Private App in{' '}
              <a href="https://app.hubspot.com/private-apps" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                HubSpot Settings
              </a>
              {' '}with CRM and Email scopes
            </p>
          </div>

          <Separator className="bg-slate-800" />

          {/* HubSpot Data Import */}
          <div className="space-y-3" data-testid="hubspot-import-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Plug className="w-5 h-5 text-orange-400" />
                <div>
                  <p className="font-medium text-white">Import data from HubSpot</p>
                  <p className="text-xs text-slate-500">Pull contacts, companies, custom fields & history into Care Tech Compare</p>
                </div>
              </div>
              {hsImportJob && ['queued', 'running'].includes(hsImportJob.status) ? (
                <div className="flex items-center gap-2">
                  <Badge className="bg-amber-500/20 text-amber-400 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> {hsImportJob.phase || 'running'}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={cancelHubspotImport} className="text-slate-400 hover:text-red-400 h-7 px-2" data-testid="cancel-hubspot-import-btn">
                    Cancel
                  </Button>
                </div>
              ) : hsImportJob?.status === 'completed' ? (
                <Badge className="bg-emerald-500/20 text-emerald-400">Last import complete</Badge>
              ) : hsImportJob?.status === 'interrupted' ? (
                <Badge className="bg-orange-500/20 text-orange-400">Interrupted - resumable</Badge>
              ) : hsImportJob?.status === 'failed' ? (
                <Badge className="bg-red-500/20 text-red-400">Failed</Badge>
              ) : null}
            </div>

            {(!hsImportJob || !['queued', 'running'].includes(hsImportJob.status)) && (
              <div className="space-y-2">
                {!userIntegrations?.hubspot_connected && !['interrupted', 'failed'].includes(hsImportJob?.status) && (
                  <Input
                    type="password"
                    value={hubspotKey}
                    onChange={(e) => setHubspotKey(e.target.value)}
                    placeholder="Paste your HubSpot Private App token (CRM read scopes)"
                    className="bg-slate-950 border-slate-800"
                    data-testid="hubspot-import-token-input"
                  />
                )}
                <div className="flex gap-2">
                  {['interrupted', 'failed'].includes(hsImportJob?.status) && (
                    <Button
                      onClick={() => startHubspotImport(true)}
                      disabled={startingImport}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="resume-hubspot-import-btn"
                    >
                      {startingImport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
                      Resume import
                    </Button>
                  )}
                  <Button
                    onClick={() => startHubspotImport(false)}
                    disabled={startingImport}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="start-hubspot-import-btn"
                  >
                    {startingImport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
                    {['interrupted', 'failed'].includes(hsImportJob?.status) ? 'Restart from scratch' : 'Import from HubSpot'}
                  </Button>
                </div>
              </div>
            )}

            {hsImportJob && (
              <div className="text-xs text-slate-400 bg-slate-950/50 border border-slate-800 rounded-lg p-3" data-testid="hubspot-import-progress">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                  <div><div className="text-white font-semibold">{hsImportJob.counts?.companies ?? 0}</div>Companies</div>
                  <div><div className="text-white font-semibold">{hsImportJob.counts?.contacts ?? 0}</div>Contacts</div>
                  <div><div className="text-white font-semibold">{hsImportJob.counts?.custom_fields ?? 0}</div>Custom fields</div>
                  <div><div className="text-white font-semibold">{hsImportJob.counts?.activities ?? 0}</div>Activities</div>
                </div>
                {hsImportJob.error && <p className="text-red-400 mt-2">{hsImportJob.error}</p>}
              </div>
            )}
            <p className="text-xs text-slate-600">Read-only - nothing in your HubSpot account is changed. Re-running updates existing records.</p>
          </div>

          <Separator className="bg-slate-800" />

          {/* Better Proposals */}
          <div className="space-y-3" data-testid="better-proposals-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Copy className="w-5 h-5 text-indigo-400" />
                <div>
                  <p className="font-medium text-white">Better Proposals</p>
                  <p className="text-xs text-slate-500">Create & track proposals from your CRM</p>
                </div>
              </div>
              {bpConnected ? (
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-500/20 text-emerald-400">Connected</Badge>
                  <Button variant="ghost" size="sm" onClick={disconnectBp} disabled={savingBp} className="text-slate-400 hover:text-red-400" data-testid="disconnect-bp-btn">
                    {savingBp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                  </Button>
                </div>
              ) : (
                <Badge className="bg-slate-700/50 text-slate-400">Not connected</Badge>
              )}
            </div>
            {!bpConnected && (
              <div className="flex gap-2">
                <Input
                  type="password"
                  value={bpToken}
                  onChange={(e) => setBpToken(e.target.value)}
                  placeholder="Enter your Better Proposals API token"
                  className="bg-slate-950 border-slate-800 flex-1"
                  data-testid="bp-token-input"
                />
                <Button onClick={connectBp} disabled={savingBp} className="bg-indigo-600 hover:bg-indigo-700" data-testid="connect-bp-btn">
                  {savingBp ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Connect'}
                </Button>
              </div>
            )}
            <p className="text-xs text-slate-600">
              Generate your API token in{' '}
              <a href="https://betterproposals.io/2/integrations/api/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Better Proposals → Integrations → API</a>
              {' '}(Premium/Enterprise plans).
            </p>
            {bpConnected && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-emerald-400/90 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2" data-testid="bp-auto-sync-note">
                <div className="flex items-center gap-2 min-w-0">
                  <RefreshCw className="w-3.5 h-3.5 shrink-0" />
                  <span>
                    Auto-syncs to your deals every day.
                    {bpAutoSync?.last_run
                      ? ` Last run ${new Date(bpAutoSync.last_run).toLocaleString()}${bpAutoSync.last_result ? ` - ${bpAutoSync.last_result.updated || 0} updated, ${bpAutoSync.last_result.created || 0} new.` : '.'}`
                      : ' First run within 24 hours.'}
                  </span>
                </div>
                <Button onClick={syncBpNow} disabled={syncingBp} variant="outline" size="sm" className="bg-slate-900 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 shrink-0" data-testid="bp-sync-now-btn">
                  {syncingBp ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                  Sync now
                </Button>
              </div>
            )}
          </div>
          </>
          )}


          <Separator className="bg-slate-800" />

          {/* Companies House */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="font-medium text-white">Companies House</p>
                  <p className="text-xs text-slate-500">UK company search & data</p>
                </div>
              </div>
              {userIntegrations?.companies_house_connected ? (
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-500/20 text-emerald-400">Connected</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => disconnectIntegration('companies_house')}
                    disabled={savingIntegration === 'companies_house'}
                    className="text-slate-400 hover:text-red-400"
                  >
                    {savingIntegration === 'companies_house' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Unlink className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ) : (
                <Badge className="bg-slate-700/50 text-slate-400">Not connected</Badge>
              )}
            </div>
            {!userIntegrations?.companies_house_connected && (
              <div className="flex gap-2">
                <Input
                  type="password"
                  value={companiesHouseKey}
                  onChange={(e) => setCompaniesHouseKey(e.target.value)}
                  placeholder="Enter your Companies House API key"
                  className="bg-slate-950 border-slate-800 flex-1"
                  data-testid="companies-house-key-input"
                />
                <Button
                  onClick={saveCompaniesHouseKey}
                  disabled={savingIntegration === 'companies_house'}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  data-testid="connect-companies-house-btn"
                >
                  {savingIntegration === 'companies_house' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Connect'
                  )}
                </Button>
              </div>
            )}
            <p className="text-xs text-slate-600">
              Get your API key from{' '}
              <a href="https://developer.company-information.service.gov.uk/get-started" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                Companies House Developer Hub
              </a>
              {' '}(free registration required)
            </p>
          </div>

          <Separator className="bg-slate-800" />

          {/* Hunter.io */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-amber-400" />
                <div>
                  <p className="font-medium text-white">Hunter.io</p>
                  <p className="text-xs text-slate-500">Find professional email addresses</p>
                </div>
              </div>
              {userIntegrations?.hunter_connected ? (
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-500/20 text-emerald-400">Connected</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => disconnectIntegration('hunter')}
                    disabled={savingIntegration === 'hunter'}
                    className="text-slate-400 hover:text-red-400"
                  >
                    {savingIntegration === 'hunter' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Unlink className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ) : (
                <Badge className="bg-slate-700/50 text-slate-400">Not connected</Badge>
              )}
            </div>
            {!userIntegrations?.hunter_connected && (
              <div className="flex gap-2">
                <Input
                  type="password"
                  value={hunterKey}
                  onChange={(e) => setHunterKey(e.target.value)}
                  placeholder="Enter your Hunter.io API key"
                  className="bg-slate-950 border-slate-800 flex-1"
                  data-testid="hunter-key-input"
                />
                <Button
                  onClick={saveHunterKey}
                  disabled={savingIntegration === 'hunter'}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="connect-hunter-btn"
                >
                  {savingIntegration === 'hunter' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Connect'
                  )}
                </Button>
              </div>
            )}
            <p className="text-xs text-slate-600">
              Get your API key from{' '}
              <a href="https://hunter.io/api_keys" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                Hunter.io Dashboard
              </a>
              {' '}(free tier available with 25 searches/month)
            </p>
          </div>

          <Separator className="bg-slate-800" />

          {/* Sender Email */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className="font-medium text-white">Sender Email Address</p>
                  <p className="text-xs text-slate-500">The "From" address for your outreach emails</p>
                </div>
              </div>
              {userIntegrations?.sender_email && (
                <Badge className="bg-emerald-500/20 text-emerald-400">Configured</Badge>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                placeholder="e.g., outreach@yourdomain.com"
                className="bg-slate-950 border-slate-800 flex-1"
                data-testid="sender-email-input"
              />
              <Button
                onClick={saveSenderEmail}
                disabled={savingSenderEmail}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="save-sender-email-btn"
              >
                {savingSenderEmail ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
              </Button>
            </div>
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-xs text-amber-400">
                <strong>Important:</strong> Your domain must be verified with Resend before emails can be sent. 
                Add SPF and DKIM records at your domain registrar. Visit{' '}
                <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-300">
                  resend.com/domains
                </a>
                {' '}to verify your domain.
              </p>
            </div>
          </div>

          <Separator className="bg-slate-800" />

          {/* Email Signature */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-pink-400" />
                <div>
                  <p className="font-medium text-white">Email Signature (HTML)</p>
                  <p className="text-xs text-slate-500">Custom HTML signature appended to all outreach emails</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {userIntegrations?.email_signature && (
                  <Badge className="bg-emerald-500/20 text-emerald-400">Configured</Badge>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSignaturePreview(!showSignaturePreview)}
                  className="border-slate-700 text-slate-300"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  {showSignaturePreview ? 'Hide' : 'Preview'}
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              <textarea
                value={emailSignature}
                onChange={(e) => setEmailSignature(e.target.value)}
                placeholder="Paste your HTML signature here... (e.g., from HubSpot Signature Generator)"
                className="w-full min-h-[200px] bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500/50 font-mono"
                data-testid="email-signature-input"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  Tip: Use{' '}
                  <a href="https://www.hubspot.com/email-signature-generator" target="_blank" rel="noopener noreferrer" className="text-pink-400 underline hover:text-pink-300">
                    HubSpot's free signature generator
                  </a>
                  {' '}to create a professional signature
                </p>
                <Button
                  onClick={saveEmailSignature}
                  disabled={savingSignature}
                  className="bg-pink-600 hover:bg-pink-700"
                  data-testid="save-signature-btn"
                >
                  {savingSignature ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Signature
                </Button>
              </div>
            </div>
            
            {/* Signature Preview */}
            {showSignaturePreview && emailSignature && (
              <div className="mt-4 p-4 bg-white rounded-lg">
                <p className="text-xs text-slate-500 mb-2 bg-slate-100 p-2 rounded">Preview (how it will appear in emails):</p>
                <div 
                  className="signature-preview"
                  dangerouslySetInnerHTML={{ __html: emailSignature }}
                />
              </div>
            )}
          </div>

          <Separator className="bg-slate-800" />

          {/* Email Booking Button */}
          <div className="space-y-3" data-testid="booking-button-settings">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CalendarClock className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="font-medium text-white">"Book a meeting" button</p>
                  <p className="text-xs text-slate-500">Adds a booking button to every outreach &amp; campaign email so prospects can see your availability and self-book.</p>
                </div>
              </div>
              <Switch
                checked={bookingButtonEnabled}
                onCheckedChange={setBookingButtonEnabled}
                data-testid="booking-button-toggle"
              />
            </div>

            {bookingButtonEnabled && (
              <div className="space-y-3 pl-8">
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Your booking page</Label>
                  {bookingPages.length === 0 ? (
                    <p className="text-xs text-amber-400">
                      You have no booking pages yet. Create one on the Meetings page - until then, emails will send without the button.
                    </p>
                  ) : (
                    <select
                      value={defaultBookingPageId}
                      onChange={(e) => setDefaultBookingPageId(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      data-testid="booking-page-select"
                    >
                      <option value="">Use my own booking page (auto)</option>
                      {bookingPages.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  )}
                  <p className="text-xs text-slate-500">
                    Meetings booked from your emails land in your own Outlook calendar.
                  </p>
                </div>
                {!publicAppUrlConfigured && (
                  <p className="text-xs text-amber-400">
                    Note: set <code className="bg-slate-800 px-1 rounded">PUBLIC_APP_URL</code> in your production environment so the booking link points to your live site.
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end">
              <Button
                onClick={saveBookingButtonSettings}
                disabled={savingBookingBtn}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-booking-button-btn"
              >
                {savingBookingBtn ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>

          <Separator className="bg-slate-800" />

          {/* Booking form - Nature of enquiry options */}
          <div className="space-y-3" data-testid="enquiry-options-settings">
            <div className="flex items-center gap-3">
              <CalendarClock className="w-5 h-5 text-violet-400" />
              <div>
                <p className="font-medium text-white">Booking form - "Nature of enquiry" options</p>
                <p className="text-xs text-slate-500">One option per line. These appear in the dropdown on your public booking pages.</p>
              </div>
            </div>
            <Textarea
              value={enquiryOptionsText}
              onChange={(e) => setEnquiryOptionsText(e.target.value)}
              rows={8}
              placeholder={"SEO (Search Engine Optimisation)\nPPC / Google Ads\nSocial Media Marketing\nOther"}
              className="bg-slate-950 border-slate-800 text-white font-mono text-sm"
              data-testid="enquiry-options-textarea"
            />
            <div className="flex justify-end">
              <Button
                onClick={saveEnquiryOptions}
                disabled={savingEnquiry}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="save-enquiry-options-btn"
              >
                {savingEnquiry ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save options
              </Button>
            </div>
          </div>

        </CardContent>
      </Card>

      {/* GDPR & Email Compliance */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="compliance-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-rose-500/10 rounded-lg">
              <ShieldCheck className="w-5 h-5 text-rose-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">GDPR &amp; Email Compliance</CardTitle>
              <CardDescription>Unsubscribes, bounces and spam complaints are suppressed across every send.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="suppression-stats">
            <div className="bg-slate-950 border border-slate-800 rounded-lg p-3">
              <p className="text-2xl font-semibold text-white" data-testid="suppressed-total">{complianceStats.suppressed_total ?? 0}</p>
              <p className="text-xs text-slate-500">Suppressed total</p>
            </div>
            {['unsubscribed', 'bounced', 'complained'].map(k => (
              <div key={k} className="bg-slate-950 border border-slate-800 rounded-lg p-3">
                <p className="text-2xl font-semibold text-white">{complianceStats.by_reason?.[k] ?? 0}</p>
                <p className="text-xs text-slate-500 capitalize">{k}</p>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Resend bounce/complaint webhook URL</Label>
            <p className="text-xs text-slate-500">In Resend → Webhooks, add this endpoint and subscribe to <span className="text-slate-300">email.bounced</span> and <span className="text-slate-300">email.complained</span>. Then add the signing secret to the backend env var <span className="text-slate-300">RESEND_WEBHOOK_SECRET</span>.</p>
            <div className="flex items-center gap-2">
              <Input readOnly value={`${process.env.REACT_APP_BACKEND_URL}/api/public/resend/webhook`} className="bg-slate-950 border-slate-800 text-slate-300 font-mono text-xs" data-testid="resend-webhook-url" />
              <Button variant="outline" className="bg-slate-900 border-slate-700 shrink-0" onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/public/resend/webhook`); toast.success('Webhook URL copied'); }} data-testid="copy-webhook-url-btn">
                Copy
              </Button>
            </div>
            <p className={`text-xs ${complianceStats.webhook_configured ? 'text-emerald-400' : 'text-amber-400'}`} data-testid="webhook-status">
              {complianceStats.webhook_configured ? 'Signature verification active (secret configured).' : 'Signing secret not set - webhook events are accepted but not signature-verified.'}
            </p>
          </div>
          <p className="text-xs text-slate-500">Set a contact's consent on the Contacts page (per-contact or in bulk). Unsubscribed, bounced, complained and non-marketing contacts are excluded from campaigns, cold outreach, sequences and auto-follow-ups.</p>
        </CardContent>
      </Card>

      </>)}

      {/* Outlook → CRM email logging */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="outlook-sync-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Mail className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">Outlook email logging</CardTitle>
              <CardDescription>When you email someone from Outlook they're auto-added to Care Tech Compare, and replies you receive are logged to the matching contact's timeline.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg px-4 py-3">
            <div>
              <p className="text-sm font-medium text-white">Auto-log sent emails &amp; replies</p>
              <p className="text-xs text-slate-500">Only emails sent/received <em>after</em> enabling are imported - no historical backfill. Replies log to existing contacts only.</p>
            </div>
            <Switch
              checked={outlookSync.enabled !== false}
              onCheckedChange={toggleOutlookSync}
              data-testid="outlook-sync-toggle"
            />
          </div>
          <p className="text-xs text-amber-400" data-testid="outlook-reconnect-note">
            New permission required: reconnect your Outlook in <strong>Meetings → Connect Outlook</strong> so Care Tech Compare can read your Sent Items.
          </p>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-slate-500" data-testid="outlook-last-sync">
              {outlookSync.last_synced_at
                ? `Last sync: ${new Date(outlookSync.last_synced_at).toLocaleString()} · ${outlookSync.last_queued_contacts || 0} to approve, ${outlookSync.last_logged_emails || 0} sent, ${outlookSync.last_logged_inbound || 0} replies`
                : 'Not synced yet.'}
            </p>
            <Button onClick={syncOutlookNow} disabled={syncingOutlook} variant="outline" className="bg-slate-900 border-slate-700" data-testid="outlook-sync-now-btn">
              {syncingOutlook ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Sync now
            </Button>
          </div>

          {/* Pending contacts to approve (from emailed recipients) */}
          <div className="border-t border-slate-800 pt-4" data-testid="contact-approvals-section">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">Contacts to approve</p>
                <p className="text-xs text-slate-500">People you've emailed who aren't in your CRM yet. Approve to add them, or dismiss to keep your CRM clean. Nobody is added automatically.</p>
              </div>
              {pendingContacts.length > 0 && (
                <Button onClick={approveAllContacts} disabled={bulkApproving} size="sm" className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid="approve-all-contacts-btn">
                  {bulkApproving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <CheckCircle className="w-3.5 h-3.5 mr-1.5" />}
                  Approve all ({pendingContacts.length})
                </Button>
              )}
            </div>
            {pendingContacts.length === 0 ? (
              <p className="text-xs text-slate-600 bg-slate-950 border border-slate-800 rounded-lg px-4 py-3" data-testid="no-pending-contacts">
                No contacts waiting for approval.
              </p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="pending-contacts-list">
                {pendingContacts.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-2 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2" data-testid={`pending-contact-${c.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate">{c.name || c.email.split('@')[0]}</p>
                      <p className="text-xs text-slate-500 truncate break-all">{c.email}</p>
                      {c.last_subject && <p className="text-[11px] text-slate-600 truncate">Re: {c.last_subject}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button onClick={() => approveContact(c.id)} disabled={approvingId === c.id} size="sm" variant="outline" className="h-8 bg-slate-900 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" data-testid={`approve-contact-${c.id}`}>
                        {approvingId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                      </Button>
                      <Button onClick={() => rejectContact(c.id)} disabled={approvingId === c.id} size="sm" variant="ghost" className="h-8 text-slate-400 hover:text-red-400" data-testid={`reject-contact-${c.id}`}>
                        <XCircle className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      {wsAdmin && (<>
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/10 rounded-lg">
              <Target className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">ICP Criteria</CardTitle>
              <CardDescription>Define your Ideal Customer Profile for lead scoring</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Industry SIC Codes */}
          <div className="space-y-3">
            <Label className="text-slate-300">Target Industries</Label>
            <div className="flex flex-wrap gap-2">
              {sicCodes.map((code) => (
                <button
                  key={code}
                  onClick={() => toggleSICCode(code)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    settings.icp_criteria?.sic_codes?.includes(code)
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600'
                  }`}
                  data-testid={`sic-${code.toLowerCase()}`}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>

          <Separator className="bg-slate-800" />

          {/* Staff Count Range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-slate-300">Min Staff Count</Label>
              <Input
                type="number"
                value={settings.icp_criteria?.min_staff || 5}
                onChange={(e) => updateICPCriteria('min_staff', parseInt(e.target.value))}
                className="bg-slate-950 border-slate-800"
                data-testid="min-staff"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Max Staff Count</Label>
              <Input
                type="number"
                value={settings.icp_criteria?.max_staff || 50}
                onChange={(e) => updateICPCriteria('max_staff', parseInt(e.target.value))}
                className="bg-slate-950 border-slate-800"
                data-testid="max-staff"
              />
            </div>
          </div>

          {/* Min Years */}
          <div className="space-y-2">
            <Label className="text-slate-300">Min. Years Incorporated</Label>
            <Input
              type="number"
              value={settings.icp_criteria?.min_years || 2}
              onChange={(e) => updateICPCriteria('min_years', parseInt(e.target.value))}
              className="bg-slate-950 border-slate-800 w-32"
              data-testid="min-years"
            />
          </div>

          <Separator className="bg-slate-800" />

          {/* Requirements */}
          <div className="space-y-4">
            <Label className="text-slate-300">Requirements</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-300">Website Required</span>
                </div>
                <Switch
                  checked={settings.icp_criteria?.require_website ?? true}
                  onCheckedChange={(v) => updateICPCriteria('require_website', v)}
                  data-testid="require-website"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-300">Portfolio Required</span>
                </div>
                <Switch
                  checked={settings.icp_criteria?.require_portfolio ?? false}
                  onCheckedChange={(v) => updateICPCriteria('require_portfolio', v)}
                  data-testid="require-portfolio"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Linkedin className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-300">LinkedIn Required</span>
                </div>
                <Switch
                  checked={settings.icp_criteria?.require_linkedin ?? true}
                  onCheckedChange={(v) => updateICPCriteria('require_linkedin', v)}
                  data-testid="require-linkedin"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Competitor Intelligence */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/10 rounded-lg">
              <Swords className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">Competitor Intelligence</CardTitle>
              <CardDescription>Track when leads leave competitor agencies</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add Competitor */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Input
              value={newCompetitor.name}
              onChange={(e) => setNewCompetitor({...newCompetitor, name: e.target.value})}
              placeholder="Competitor name"
              className="bg-slate-950 border-slate-800"
              data-testid="competitor-name"
            />
            <Input
              value={newCompetitor.domain}
              onChange={(e) => setNewCompetitor({...newCompetitor, domain: e.target.value})}
              placeholder="Domain (optional)"
              className="bg-slate-950 border-slate-800"
              data-testid="competitor-domain"
            />
            <Input
              value={newCompetitor.linkedin_url}
              onChange={(e) => setNewCompetitor({...newCompetitor, linkedin_url: e.target.value})}
              placeholder="LinkedIn URL (optional)"
              className="bg-slate-950 border-slate-800"
              data-testid="competitor-linkedin"
            />
            <Button 
              onClick={addCompetitor}
              variant="outline"
              className="border-slate-700"
              data-testid="add-competitor-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>

          {/* Competitor List */}
          {settings.competitors?.length > 0 ? (
            <div className="space-y-2">
              {settings.competitors.map((competitor, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg"
                  data-testid={`competitor-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                      <Swords className="w-4 h-4 text-red-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{competitor.name}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        {competitor.domain && <span>{competitor.domain}</span>}
                        {competitor.linkedin_url && <span>LinkedIn tracked</span>}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCompetitor(index)}
                    className="text-slate-500 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 border border-dashed border-slate-700 rounded-lg text-center">
              <Swords className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-500">No competitors added yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* General Settings */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">Outreach Settings</CardTitle>
              <CardDescription>Configure your daily sending limits</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label className="text-slate-300">Daily Send Cap</Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                value={settings.daily_send_cap || 20}
                onChange={(e) => setSettings({...settings, daily_send_cap: parseInt(e.target.value)})}
                className="bg-slate-950 border-slate-800 w-32"
                data-testid="daily-send-cap"
              />
              <span className="text-sm text-slate-500">emails per day</span>
            </div>
            <p className="text-xs text-slate-600">Recommended: 20 for optimal deliverability</p>
          </div>
        </CardContent>
      </Card>

      {/* Integrations Status */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-500/10 rounded-lg">
              <Plug className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">System Integrations</CardTitle>
              <CardDescription>Platform-level service connections</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {integrations ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Companies House */}
              <div className={`p-4 rounded-lg border ${integrations.companies_house?.configured ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-slate-800/30 border-slate-700'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-400" />
                    <span className="font-medium text-white">Companies House</span>
                  </div>
                  {integrations.companies_house?.configured ? (
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-slate-500" />
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  {integrations.companies_house?.configured 
                    ? integrations.companies_house.features.join(', ') 
                    : 'Not configured'}
                </p>
              </div>

              {/* Phantombuster - User Integration */}
              <div className={`p-4 rounded-lg border ${userIntegrations?.phantombuster_connected ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-slate-800/30 border-slate-700'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Ghost className="w-5 h-5 text-blue-400" />
                    <span className="font-medium text-white">Phantombuster</span>
                  </div>
                  {userIntegrations?.phantombuster_connected ? (
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-slate-500" />
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  {userIntegrations?.phantombuster_connected 
                    ? 'Email finding, LinkedIn automation' 
                    : 'Connect above to enable'}
                </p>
              </div>

              {/* HubSpot - User Integration */}
              <div className={`p-4 rounded-lg border ${userIntegrations?.hubspot_connected ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-slate-800/30 border-slate-700'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-orange-400" />
                    <span className="font-medium text-white">HubSpot (Your Account)</span>
                  </div>
                  {userIntegrations?.hubspot_connected ? (
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-slate-500" />
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  {userIntegrations?.hubspot_connected 
                    ? 'Email sending via your account' 
                    : 'Connect above to enable'}
                </p>
              </div>

              {/* RB2B Direct Webhook */}
              <div className="p-4 rounded-lg border bg-blue-500/5 border-blue-500/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    <span className="font-medium text-white">RB2B Direct Webhook</span>
                  </div>
                  <Badge className="bg-blue-500/20 text-blue-400">Recommended</Badge>
                </div>
                <p className="text-xs text-slate-400 mb-3">
                  Receive visitor data directly from RB2B (bypasses HubSpot for more accurate data)
                </p>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Your Webhook URL:</Label>
                  <div className="flex gap-2">
                    <Input
                      value={webhookUrl || 'Loading...'}
                      readOnly
                      className="bg-slate-950 border-slate-800 text-xs font-mono"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        if (webhookUrl) {
                          try {
                            await navigator.clipboard.writeText(webhookUrl);
                            toast.success('Webhook URL copied!');
                          } catch (err) {
                            // Fallback for when Clipboard API is blocked
                            const textArea = document.createElement('textarea');
                            textArea.value = webhookUrl;
                            textArea.style.position = 'fixed';
                            textArea.style.left = '-999999px';
                            document.body.appendChild(textArea);
                            textArea.select();
                            try {
                              document.execCommand('copy');
                              toast.success('Webhook URL copied!');
                            } catch (e) {
                              toast.info('Please copy the URL manually from the input field');
                            }
                            document.body.removeChild(textArea);
                          }
                        } else {
                          toast.error('User ID not available. Please refresh the page.');
                        }
                      }}
                      disabled={!webhookUrl}
                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    <strong>Setup:</strong> Go to <a href="https://app.rb2b.com/integrations/webhook" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">RB2B → Integrations → Webhook</a> and paste this URL
                  </p>
                </div>
              </div>

              {/* Email Tracking */}
              <div className="p-4 rounded-lg border bg-emerald-500/5 border-emerald-500/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-emerald-400" />
                    <span className="font-medium text-white">Email Tracking</span>
                  </div>
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <p className="text-xs text-slate-500">
                  {integrations.email_tracking?.features?.join(', ')}
                </p>
              </div>

              {/* Background Jobs */}
              <div className="p-4 rounded-lg border bg-emerald-500/5 border-emerald-500/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-violet-400" />
                    <span className="font-medium text-white">Background Jobs</span>
                  </div>
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <p className="text-xs text-slate-500">
                  Score recalculation every {integrations.background_jobs?.interval}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-slate-500">Loading integrations...</div>
          )}
        </CardContent>
      </Card>

      </>)}

      {/* Admin Section - Only visible to admins */}
      {user?.is_admin && (
        <Card className="bg-slate-900/50 border-red-500/30" data-testid="admin-section">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-red-400" />
              Admin Tools
            </CardTitle>
            <CardDescription className="text-slate-400">
              Administrative functions - handle with care
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <SendListingFormPanel />
            {/* Password Reset */}
            <div className="space-y-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Key className="w-4 h-4 text-red-400" />
                Reset User Password
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">User Email</Label>
                  <Input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="bg-slate-950 border-slate-800"
                    data-testid="admin-reset-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">New Password</Label>
                  <Input
                    type="password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-slate-950 border-slate-800"
                    data-testid="admin-reset-password"
                  />
                </div>
              </div>
              <Button
                onClick={handleAdminResetPassword}
                disabled={resettingPassword}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="admin-reset-btn"
              >
                {resettingPassword ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting...</>
                ) : (
                  <><Key className="w-4 h-4 mr-2" /> Reset Password</>
                )}
              </Button>
            </div>

            {/* Invite Team Member */}
            <div className="space-y-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700" data-testid="team-invite-section">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-400" />
                Add a team member
              </h3>
              <p className="text-xs text-slate-500 -mt-2">They get an email link to set their own password, then share this workspace and appear in the Funnel Inbox "Assign to" list.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Name</Label>
                  <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Jane Smith" className="bg-slate-950 border-slate-800" data-testid="invite-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Email</Label>
                  <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="jane@company.com" className="bg-slate-950 border-slate-800" data-testid="invite-email-input" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none" data-testid="invite-admin-toggle">
                <input type="checkbox" checked={inviteAdmin} onChange={(e) => setInviteAdmin(e.target.checked)} className="accent-emerald-500" />
                Make this person an admin (can manage the team & settings)
              </label>
              <Button onClick={sendInvite} disabled={sendingInvite} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-invite-btn">
                {sendingInvite ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4 mr-2" /> Send invitation</>}
              </Button>

              {invites.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-slate-700/60">
                  <p className="text-xs text-slate-500">Pending invitations ({invites.length})</p>
                  {invites.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between gap-2 p-2 bg-slate-900 rounded-lg text-sm" data-testid={`invite-row-${inv.id}`}>
                      <div className="min-w-0 flex-1">
                        <span className="text-white">{inv.name || '—'}</span>
                        <span className="text-slate-500 ml-2 break-all">{inv.email}</span>
                        {inv.make_admin && <Badge className="ml-2 bg-red-500/20 text-red-400 text-xs">Admin</Badge>}
                      </div>
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white shrink-0 h-7 px-2" onClick={() => resendInvite(inv.id)} data-testid={`invite-resend-${inv.id}`} title="Resend email">
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-400 shrink-0 h-7 px-2" onClick={() => revokeInvite(inv.id)} data-testid={`invite-revoke-${inv.id}`} title="Revoke invite">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* User List */}
            <div className="space-y-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-400" />
                All Users ({adminUsers.length})
              </h3>
              {loadingAdmin ? (
                <div className="text-slate-500 text-sm">Loading users...</div>
              ) : (
                <div className="space-y-2 max-h-[28rem] overflow-y-auto">
                  {[['team', 'Team - CRM access', 'Only these people can use the CRM.'], ['supplier', 'Supplier portal logins', 'Suppliers who claimed or created a listing. They can only use their own listing portal, never the CRM.']].map(([grp, title, hint]) => {
                    const rows = adminUsers.filter((u) => (u.role === 'supplier' ? 'supplier' : 'team') === grp);
                    return (
                    <div key={grp} className="space-y-2" data-testid={`users-group-${grp}`}>
                      <p className="text-xs font-semibold text-slate-300 uppercase tracking-wide pt-2">{title} ({rows.length})</p>
                      <p className="text-[11px] text-slate-500 -mt-1">{hint}</p>
                  {rows.map((u) => {
                    const isSelf = u.email === user?.email;
                    const isOwner = u.role === 'owner';
                    const isSupplierUser = u.role === 'supplier';
                    return (
                    <div
                      key={u.id}
                      className="flex items-center justify-between gap-2 p-2 bg-slate-900 rounded-lg text-sm"
                      data-testid={`user-row-${u.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <span className="text-white">{u.name || 'No name'}</span>
                        <span className="text-slate-500 ml-2 break-all">{u.email}</span>
                        {isOwner && <Badge className="ml-2 bg-amber-500/20 text-amber-400 text-xs">Owner</Badge>}
                        {u.is_admin && !isOwner && (
                          <Badge className="ml-2 bg-red-500/20 text-red-400 text-xs">Admin</Badge>
                        )}
                        {!isOwner && !u.is_admin && <Badge className={`ml-2 text-xs ${isSupplierUser ? 'bg-sky-500/15 text-sky-500' : 'bg-emerald-500/15 text-emerald-600'}`} data-testid={`user-type-${u.id}`}>{isSupplierUser ? 'Supplier portal' : 'Team member'}</Badge>}
                        {isSelf && <Badge className="ml-2 bg-slate-600/40 text-slate-300 text-xs">You</Badge>}
                      </div>
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white shrink-0 h-7 px-2"
                        onClick={() => setResetEmail(u.email)} title="Use in password reset above">
                        Select
                      </Button>
                      {!isOwner && !isSelf && !isSupplierUser && (
                        <Button size="sm" variant="ghost"
                          className="text-slate-400 hover:text-white shrink-0 h-7 px-2"
                          onClick={() => toggleUserAdmin(u)}
                          data-testid={`user-toggle-admin-${u.id}`}
                          title={u.is_admin ? 'Remove admin rights' : 'Make admin'}>
                          {u.is_admin ? 'Demote' : 'Make admin'}
                        </Button>
                      )}
                      {!isOwner && !isSelf && (
                        <Button size="sm" variant="ghost"
                          className="text-slate-400 hover:text-red-400 shrink-0 h-7 px-2"
                          onClick={() => removeUser(u)}
                          data-testid={`user-remove-${u.id}`}
                          title={isSupplierUser ? 'Delete this portal login' : 'Remove from team'}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                    );
                  })}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Settings;
